import { Box, Grid, IconButton, makeStyles, MobileStepper, Typography, useMediaQuery } from "@material-ui/core";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import clsx from "clsx";
import { FC, ReactNode, useState } from "react";
import SwipeableViews from "react-swipeable-views";
import { Product } from "../../../../api";
import { usePrimarySatProduct } from "../../../../common/helpers";
import { useProductStyles, useSharedStyles } from "../../../../common/sharedStyles";
import { useSatSteps } from "../../../../hooks";
import { ReactComponent as AdvisersKsSvg } from "../../../../images/advisers-ks.svg";
import { ReactComponent as AdvisersWbSvg } from "../../../../images/advisers-wb.svg";
import { ReactComponent as KsBalancedSvg } from "../../../../images/asset-allocation-ks-balanced.svg";
import { ReactComponent as KsLifeCycleSvg } from "../../../../images/asset-allocation-ks-lifecycle.svg";
import { ReactComponent as KsSelfSelectSvg } from "../../../../images/asset-allocation-ks-selfselect.svg";
import { ReactComponent as WbLifeCycleSvg } from "../../../../images/asset-allocation-wb-lifecycle.svg";
import { ReactComponent as WbSelfSelectSvg } from "../../../../images/asset-allocation-wb-selfselect.svg";
import { ReactComponent as LogoImage } from "../../../../images/nz-funds-nz-logo.svg";
import { ReactComponent as NzFunds } from "../../../../images/nz-funds.svg";
import { ReactComponent as PhoneKsSvg } from "../../../../images/phone-ks.svg";
import { ReactComponent as PhoneWbSvg } from "../../../../images/phone-wb.svg";
import { ReactComponent as TickKsSvg } from "../../../../images/tick-ks.svg";
import { ReactComponent as TickWbSvg } from "../../../../images/tick-wb.svg";
import theme from "../../../../theme";
import { Colors, TitleText } from "../../../controls/helpers";
import { Body } from "../../GridLayout";
import QuestionnaireScreen from "../../QuestionnaireScreen";

enum AssetAllocation {
  Automated = "Automated",
  Fixed = "Fixed",
  Manual = "Manual",
}

enum ManagementType {
  Passive = "Passive",
  Active = "Active",
}

interface SectionData {
  name: string;
  who: string;
  assetAllocation: AssetAllocation;
  managementType: ManagementType;
  management: string;
  advice: string;
  fees: string | ReactNode;
  AssetAllocationImage: FC;
  InvestmentSvg: FC;
  AdviceSvg: FC;
}

interface LargeScreenViewProps {
  steps: SectionData[];
}

interface SmallScreenViewProps {
  step: SectionData;
}

const AssetAllocationOptions = () => {
  const classes = useStyles();
  const sharedClasses = useSharedStyles();
  const { currentStep } = useSatSteps();
  const isKs = currentStep.route.toLowerCase().indexOf("ks") !== -1;
  const productClasses = useProductStyles({ product: isKs ? Product.KiwiSaver : Product.WealthBuilder });
  const [activeStep, setActiveStep] = useState(0);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const steps: SectionData[] = [
    {
      name: "Self Select",
      who: "Self Select allows you to customise your asset allocation to suit your needs",
      assetAllocation: AssetAllocation.Manual,
      managementType: ManagementType.Active,
      management: "Access to three actively managed strategies with downside mitigation",
      advice: "Personal financial advice",
      fees: "1.12–1.65%",
      AssetAllocationImage: isKs ? KsSelfSelectSvg : WbSelfSelectSvg,
      InvestmentSvg: isKs ? TickKsSvg : TickWbSvg,
      AdviceSvg: isKs ? AdvisersKsSvg : AdvisersWbSvg,
    },
    {
      name: "Life Cycle",
      who: "Our Life Cycle approach automatically adjusts your asset allocation as you age",
      assetAllocation: AssetAllocation.Automated,
      managementType: ManagementType.Active,
      management: "Automatic allocation into three actively managed strategies with downside mitigation",
      advice: "Personal financial advice",
      fees: "1.39**–1.60%",
      AssetAllocationImage: isKs ? KsLifeCycleSvg : WbLifeCycleSvg,
      InvestmentSvg: isKs ? TickKsSvg : TickWbSvg,
      AdviceSvg: isKs ? AdvisersKsSvg : AdvisersWbSvg,
    },
  ];

  if (isKs) {
    steps.unshift({
      name: "Balanced",
      who: "The Balanced Fund is a zero management fee option available to everyone",
      assetAllocation: AssetAllocation.Fixed,
      managementType: ManagementType.Passive,
      management: "Passively managed with no downside mitigation",
      advice: "Access our call centre",
      fees: "0%",
      AssetAllocationImage: KsBalancedSvg,
      InvestmentSvg: isKs ? TickKsSvg : TickWbSvg,
      AdviceSvg: isKs ? PhoneKsSvg : PhoneWbSvg,
    });
  }

  const maxSteps = steps.length;

  return (
    <QuestionnaireScreen>
      <Body isNarrowView className={classes.body}>
        <TitleText className={classes.title}>
          NZ Funds offers {maxSteps === 2 ? "two" : "three"} asset allocation options within {isKs ? "our KiwiSaver Scheme" : "Wealth Builder"}.
        </TitleText>
        {!isMobile && <LargeScreenView steps={steps} />}
        {isMobile && (
          <Box display="flex" flexGrow={0} flexDirection="column">
            <Box display="flex" alignItems="center" className={clsx(sharedClasses.white, productClasses.backgroundColor)}>
              <Box p={1}>
                <IconButton onClick={(_) => activeStep !== 0 && setActiveStep((_activeStep) => --_activeStep)} className={activeStep === 0 ? productClasses.color : sharedClasses.white}>
                  <ChevronLeftIcon />
                </IconButton>
              </Box>
              <Box display="flex" flexGrow={1} flexDirection="column" justifyContent="space-between" minHeight="100%">
                <Box height={16}>&nbsp;</Box>
                <Box textAlign="center">
                  <Typography variant="h6">
                    {activeStep + 1}. {steps[activeStep].name}
                  </Typography>
                </Box>
                <Box>
                  <MobileStepper
                    className={classes.stepper}
                    classes={{ root: productClasses.backgroundColor, dotActive: productClasses.medBackgroundColor }}
                    steps={maxSteps}
                    variant="dots"
                    position="static"
                    activeStep={activeStep}
                    backButton={<>&nbsp;</>}
                    nextButton={<>&nbsp;</>}
                  />
                </Box>
              </Box>
              <Box p={1}>
                <IconButton
                  onClick={(_) => activeStep !== maxSteps - 1 && setActiveStep((_activeStep) => ++_activeStep)}
                  className={activeStep === maxSteps - 1 ? productClasses.color : sharedClasses.white}
                >
                  <ChevronRightIcon />
                </IconButton>
              </Box>
            </Box>
            <SwipeableViews index={activeStep} onChangeIndex={(step) => setActiveStep(step)}>
              {steps.map((step) => (
                <SmallScreenView key={step.name} step={step} />
              ))}
            </SwipeableViews>
          </Box>
        )}
      </Body>
    </QuestionnaireScreen>
  );
};

const LargeScreenView: FC<LargeScreenViewProps> = ({ steps }) => {
  const sharedClasses = useSharedStyles();
  const { currentStep } = useSatSteps();
  const isKs = currentStep.route.toLowerCase().indexOf("ks") !== -1;
  const productClasses = useProductStyles({ product: isKs ? Product.KiwiSaver : Product.WealthBuilder });
  const classes = useStyles();
  const maxSteps = steps.length;

  return (
    <Grid container>
      <Grid item xs={12}>
        <Grid container>
          {steps.map(({ name }, index) => (
            <Grid item md={maxSteps === 2 ? 6 : 4} className={productClasses.backgroundColor} key={index} style={{ textAlign: "center" }}>
              <Box display="inline-flex" borderRadius={30} m={1} p={1} bgcolor={Colors.White}>
                <Box borderRadius="50%" width={32} height={32} className={productClasses.backgroundColor} marginRight={1}>
                  <Typography variant="h6" align="center" className={sharedClasses.white}>
                    <b>{index + 1}</b>
                  </Typography>
                </Box>
                <Box pr={1}>
                  <Typography variant="h6">
                    <b>{name}</b>
                  </Typography>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Box pt={2}>
          <Typography className={clsx(sharedClasses.text, productClasses.color)} align="center">
            Who's it for?
          </Typography>
        </Box>
      </Grid>
      <Grid item xs={12} className={clsx(productClasses.lightBorderColor, classes.borderBottom)}>
        <Grid container>
          {steps.map(({ who }, index) => (
            <Grid item md={maxSteps === 2 ? 6 : 4} key={index}>
              <Box p={2}>
                <Typography className={clsx(sharedClasses.text, sharedClasses.black)} align="center">
                  {who}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Box p={2}>
          <Typography className={clsx(sharedClasses.text, productClasses.color)} align="center">
            Asset allocation
          </Typography>
        </Box>
      </Grid>
      <Grid item xs={12} className={clsx(productClasses.lightBorderColor, classes.borderBottom)}>
        <Grid container>
          {steps.map(({ AssetAllocationImage, assetAllocation }, index) => (
            <Grid item md={maxSteps === 2 ? 6 : 4} key={index}>
              <Box pt={1} px={1} textAlign="center">
                <AssetAllocationImage />
              </Box>
              <Box p={2}>
                <Typography className={clsx(sharedClasses.text, sharedClasses.black)} align="center">
                  {assetAllocation}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Box py={2}>
          <Typography className={clsx(sharedClasses.text, productClasses.color)} align="center">
            Management style
          </Typography>
        </Box>
      </Grid>
      <Grid item xs={12} className={clsx(productClasses.lightBorderColor, classes.borderBottom)}>
        <Grid container>
          {steps.map(({ management, managementType }, index) => (
            <Grid item md={maxSteps === 2 ? 6 : 4} key={index}>
              <Box textAlign="center">
                <Typography className={clsx(sharedClasses.text, productClasses.color)}>{managementType}</Typography>
              </Box>
              <Box px={2} pb={2} textAlign="center">
                <Typography className={clsx(sharedClasses.text, sharedClasses.black)}>{management}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Box pt={2}>
          <Typography className={clsx(sharedClasses.text, productClasses.color)} align="center">
            Responsible investment
          </Typography>
        </Box>
      </Grid>
      <Grid item xs={12} className={clsx(productClasses.lightBorderColor, classes.borderBottom)}>
        <Grid container>
          {steps.map(({ InvestmentSvg }, index) => (
            <Grid item md={maxSteps === 2 ? 6 : 4} key={index}>
              <Box pb={2} textAlign="center">
                <InvestmentSvg />
              </Box>
            </Grid>
          ))}
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Box pt={2}>
          <Typography className={clsx(sharedClasses.text, productClasses.color)} align="center">
            What if I need advice?
          </Typography>
        </Box>
      </Grid>
      <Grid item xs={12} className={clsx(productClasses.lightBorderColor, classes.borderBottom)}>
        <Grid container>
          {steps.map(({ advice, AdviceSvg }, index) => (
            <Grid item md={maxSteps === 2 ? 6 : 4} key={index}>
              <Box pt={1} px={1} textAlign="center">
                <AdviceSvg />
              </Box>
              <Box pb={2} textAlign="center">
                <Typography className={clsx(sharedClasses.text, sharedClasses.black)}>{advice}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Grid>
      {/* <Grid item xs={12} className={clsx(productClasses.lightBorderColor, productClasses.backgroundColor)}>
        <Box py={2}>
          <Typography className={clsx(sharedClasses.text, sharedClasses.white)} align="center">
            Management fees*
          </Typography>
        </Box>
      </Grid> */}
      {/* <Grid item xs={12}>
        <Grid container>
          {steps.map(({ fees, name }, index) => (
            <Grid item md={maxSteps === 2 ? 6 : 4} key={index} className={clsx(productClasses.backgroundColor, productClasses.lightBorderColor)} style={{ textAlign: "center" }}>
              <Box px={3}>
                <Box py={1} borderRadius={30} display="inline-flex" width="100%" bgcolor={Colors.White}>
                  <Typography style={{ width: "100%" }} variant="h5" className={productClasses.color}>
                    <b>{fees}</b>
                  </Typography>
                </Box>
              </Box>
              <Box py={3} px={2}>
                <Typography className={clsx(sharedClasses.smallPrint, sharedClasses.white)}>*Estimated annual fund charges as disclosed in the current PDS.</Typography>
                {name === "Life Cycle" && <Typography className={clsx(sharedClasses.smallPrint, sharedClasses.white)}>**At age 75.</Typography>}
              </Box>
            </Grid>
          ))}
        </Grid>
      </Grid> */}
      <Grid item xs={12} className={clsx(productClasses.lightBorderColor, classes.borderBottom)}>
        <Grid container>
          <Grid item sm={2} xs={5}>
            <Box p={3}>
              <LogoImage width={76} height={76} fill={sharedClasses.black} />
            </Box>
          </Grid>
          <Grid item sm={9} xs={7}>
            <Box pl={0} pt={3} pb={2}>
              <NzFunds height={25} fill={sharedClasses.black} />
            </Box>
            <Box pl={3.2} pt={0.2}>
              <Typography className={clsx(sharedClasses.smallPrint, sharedClasses.black)}>
                Different funds have different risk profiles. See the NZ Funds {isKs ? "KiwiSaver Scheme" : "Wealth Builder"} Product Disclosure Statement for more information on risk, or talk to your
                Financial Advice Provider.
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

const SmallScreenView: FC<SmallScreenViewProps> = (props) => {
  const sharedClasses = useSharedStyles();
  const { currentStep } = useSatSteps();
  const isKs = currentStep.route.toLowerCase().indexOf("ks") !== -1;
  const productClasses = useProductStyles({ product: isKs ? Product.KiwiSaver : Product.WealthBuilder });
  const {
    step: { AssetAllocationImage, name, who, assetAllocation, managementType, management, fees, InvestmentSvg, AdviceSvg, advice },
  } = props;

  return (
    <>
      <Section>
        <Box py={1} px={3}>
          <Typography className={clsx(sharedClasses.text, productClasses.color)}>Who's it for?</Typography>
        </Box>
        <Box py={1} px={6}>
          <Typography className={clsx(sharedClasses.text, sharedClasses.black)}>{who}</Typography>
        </Box>
      </Section>
      <Section>
        <Box py={1} px={3}>
          <Typography className={clsx(sharedClasses.text, productClasses.color)}>Asset allocation</Typography>
        </Box>
        <Box pt={1} px={3}>
          <AssetAllocationImage />
        </Box>
        <Box py={1} px={3}>
          <Typography className={clsx(sharedClasses.text, sharedClasses.black)}>{assetAllocation}</Typography>
        </Box>
      </Section>
      <Section>
        <Box py={1} px={3}>
          <Typography className={clsx(sharedClasses.text, productClasses.color)}>{managementType} management</Typography>
        </Box>
        <Box py={1} px={10}>
          <Typography className={clsx(sharedClasses.text, sharedClasses.black)}>{management}</Typography>
        </Box>
      </Section>
      <Section>
        <Box py={1} px={3}>
          <Typography className={clsx(sharedClasses.text, productClasses.color)}>Responsible investment</Typography>
        </Box>
        <Box py={1} px={6}>
          <InvestmentSvg />
        </Box>
      </Section>
      <Section>
        <Box py={1} px={3}>
          <Typography className={clsx(sharedClasses.text, productClasses.color)}>What if I need advice?</Typography>
        </Box>
        <Box py={1} px={6}>
          <AdviceSvg />
        </Box>
        <Box py={1} px={2} textAlign="center">
          <Typography className={clsx(sharedClasses.text, sharedClasses.black)}>{advice}</Typography>
        </Box>
      </Section>
      <Section className={productClasses.backgroundColor}>
        <Box py={1} px={3}>
          <Typography className={clsx(sharedClasses.text, sharedClasses.white)}>Management fees*</Typography>
        </Box>
        <Box py={1} px={4} borderRadius={30} bgcolor={Colors.White}>
          <Typography variant="h5" className={productClasses.color} style={{ fontWeight: 500 }}>
            {fees}
          </Typography>
        </Box>
        <Box py={2} px={9}>
          <Typography className={clsx(sharedClasses.smallPrint, sharedClasses.white)}>*Estimated annual fund charges as disclosed in the current PDS.</Typography>
          {name === "Life Cycle" && <Typography className={clsx(sharedClasses.smallPrint, sharedClasses.white)}>**At age 75.</Typography>}
        </Box>
      </Section>
      <Box display="flex">
        <Box p={1} />
        <Box p={1} display="flex" alignItems="center" textAlign="center" flexDirection="column" width="100%">
          <Box textAlign="center">
            <NzFunds width={76} height={36} fill={sharedClasses.black} />
          </Box>
          <Box pt={1} px={3}>
            <Typography className={clsx(sharedClasses.smallPrint, sharedClasses.black)}>
              Different funds have different risk profiles. See the NZ Funds {isKs ? "KiwiSaver Scheme" : "Wealth Builder"} Product Disclosure Statement for more information on risk, or talk to your
              financial adviser.
            </Typography>
          </Box>
        </Box>
        <Box p={1} />
      </Box>
    </>
  );
};

const Section: FC<{ className?: string }> = (props) => {
  const classes = useStyles();
  const productClasses = useProductStyles({ product: usePrimarySatProduct() });
  return (
    <Box display="flex" className={props.className}>
      <Box p={1} className={clsx(classes.borderBox, productClasses.lightBorderColor)} />
      <Box p={1} display="flex" alignItems="center" textAlign="center" flexDirection="column" width="100%" className={clsx(classes.borderBox, productClasses.lightBorderColor)}>
        {props.children}
      </Box>
      <Box p={1} className={clsx(classes.borderBox, productClasses.lightBorderColor)} />
    </Box>
  );
};

const useStyles = makeStyles(
  {
    body: {
      padding: "12px 0 0 !important",
      "@media(min-width: 768px)": {
        padding: "18px 0 0 !important",
      },
    },
    borderBox: {
      borderLeftStyle: "none",
      borderBottomStyle: "none",
      borderStyle: "solid",
      borderWidth: 1,
      "&:last-child": {
        borderRightStyle: "none",
      },
    },
    stepper: {
      height: 16,
    },
    borderBottom: {
      borderBottom: "1px solid",
    },
    title: {
      padding: "0 24px",
    },
  },
  { name: "AssetAllocationOptions" }
);

export default AssetAllocationOptions;
